import React, { useState } from 'react';
import './ExportButton.css';

/**
 * ExportButton - Reusable CSV export button for analytics data
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of data objects to export
 * @param {Array} props.headers - Array of header objects: [{ key: 'field', label: 'Display Name' }]
 * @param {string} props.filename - Base filename (without .csv extension)
 * @param {string} props.label - Button label text (default: "Export CSV")
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {Function} props.onExportStart - Callback when export starts
 * @param {Function} props.onExportComplete - Callback when export completes
 * @param {Function} props.onExportError - Callback when export fails
 */
const ExportButton = ({
  data = [],
  headers = [],
  filename = 'export',
  label = 'Export CSV',
  disabled = false,
  onExportStart,
  onExportComplete,
  onExportError
}) => {
  const [isExporting, setIsExporting] = useState(false);

  /**
   * Escape CSV field value
   * - Wrap in quotes if contains comma, quote, or newline
   * - Escape quotes by doubling them
   */
  const escapeCSVField = (field) => {
    if (field === null || field === undefined) {
      return '';
    }

    const stringValue = String(field);
    
    // Check if field needs escaping
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      // Escape quotes by doubling them
      const escaped = stringValue.replace(/"/g, '""');
      return `"${escaped}"`;
    }

    return stringValue;
  };

  /**
   * Convert data array to CSV string
   */
  const convertToCSV = (dataArray, headerArray) => {
    if (!dataArray || dataArray.length === 0) {
      throw new Error('No data to export');
    }

    if (!headerArray || headerArray.length === 0) {
      throw new Error('No headers defined for export');
    }

    // Create header row
    const headerRow = headerArray.map(h => escapeCSVField(h.label)).join(',');

    // Create data rows
    const dataRows = dataArray.map(row => {
      return headerArray.map(header => {
        const value = row[header.key];
        return escapeCSVField(value);
      }).join(',');
    });

    // Combine header and data rows
    return [headerRow, ...dataRows].join('\n');
  };

  /**
   * Trigger browser download of CSV file
   */
  const downloadCSV = (csvContent, fileName) => {
    // Create blob with UTF-8 BOM for Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const fullFilename = `${fileName}_${timestamp}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', fullFilename);
    link.style.visibility = 'hidden';

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  };

  /**
   * Handle export button click
   */
  const handleExport = async () => {
    if (isExporting || disabled) {
      return;
    }

    setIsExporting(true);

    try {
      // Notify export start
      if (onExportStart) {
        onExportStart();
      }

      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 100));

      // Convert data to CSV
      const csvContent = convertToCSV(data, headers);

      // Trigger download
      downloadCSV(csvContent, filename);

      // Notify export complete
      if (onExportComplete) {
        onExportComplete();
      }
    } catch (error) {
      console.error('CSV export failed:', error);

      // Notify export error
      if (onExportError) {
        onExportError(error);
      } else {
        // Default error handling
        alert(`Export failed: ${error.message}`);
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      className={`export-button ${isExporting ? 'export-button--loading' : ''}`}
      onClick={handleExport}
      disabled={disabled || isExporting || !data || data.length === 0}
      aria-label={isExporting ? 'Exporting data...' : label}
      title={data && data.length > 0 ? `Export ${data.length} rows to CSV` : 'No data to export'}
    >
      {isExporting ? (
        <>
          <span className="export-button__spinner" aria-hidden="true"></span>
          <span className="export-button__text">Exporting...</span>
        </>
      ) : (
        <>
          <svg
            className="export-button__icon"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M8 11L4 7H6V3H10V7H12L8 11Z"
              fill="currentColor"
            />
            <path
              d="M2 13H14V15H2V13Z"
              fill="currentColor"
            />
          </svg>
          <span className="export-button__text">{label}</span>
        </>
      )}
    </button>
  );
};

export default ExportButton;
